// src/pages/Onboarding.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
} from "firebase/firestore";
import "../components/landing.css";

// Select moderna (stile coerente con landing.css)
function Select({ value, onChange, children }) {
  return (
    <div className="select-wrap">
      <select className="select-modern" value={value} onChange={onChange}>
        {children}
      </select>

      <svg
        className="select-chevron"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        aria-hidden="true"
      >
        <path
          d="M7 10l5 5 5-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

const sectors = [
  "Manufacturing",
  "Agriculture/Food",
  "Textile/Fashion",
  "Tech",
  "Finance",
  "Construction",
  "Furniture",
  "Transportation",
];

const sizes = ["1-9", "10-49", "50-249", "250+"];

const regions = [
  {
    value: "italy",
    label: "Italy",
  },
  {
    value: "eu-excluding-italy",
    label: "European Union — excluding Italy",
  },
  {
    value: "outside-eu",
    label: "Outside the European Union",
  },
];

export default function Onboarding() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    sector: "",
    size: "",
    region: "",
    csrd: "Unsure",
    goal: "compliance",
    timeline: "6-12",
  });

  /*
    Prefill existing profile values.

    A user is redirected to the dashboard only when the company setup
    is genuinely complete. This prevents older test accounts that were
    marked as onboarded without sector or company details from becoming
    unable to reopen onboarding.
  */
  useEffect(() => {
    const load = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() : {};
        const profile = data?.profile || {};

        const hasCompletedSetup =
          data?.onboardingCompleted === true &&
          Boolean(profile.sector?.trim()) &&
          Boolean(profile.size?.trim()) &&
          Boolean(profile.region?.trim());

        if (hasCompletedSetup) {
          navigate("/dashboard", { replace: true });
          return;
        }

        if (data?.profile) {
          /*
            country and turnover were used in the previous version.
            They are intentionally not loaded into the new onboarding state.
          */
          const { country, turnover, ...currentProfile } = data.profile;

          setForm((prev) => ({
            ...prev,
            ...currentProfile,
          }));
        }
      } catch (error) {
        console.error(error);
        setErr("Unable to load your company profile.");
      }
    };

    load();
  }, [navigate]);

  const update = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const canNext1 = Boolean(form.sector && form.size);
  const canNext2 = Boolean(form.region && form.csrd);

  const removeObsoleteProfileFields = async (ref) => {
    await updateDoc(ref, {
      "profile.country": deleteField(),
      "profile.turnover": deleteField(),
    });
  };

  const finish = async () => {
    setErr("");
    setBusy(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("User not authenticated.");

      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      const previousData = snap.exists() ? snap.data() : {};

      await setDoc(
        ref,
        {
          uid: user.uid,
          email: user.email || "",
          name:
            user.displayName ||
            previousData.name ||
            user.email?.split("@")[0] ||
            "",
          profile: {
            sector: form.sector,
            size: form.size,
            region: form.region,
            csrd: form.csrd,
            goal: form.goal,
            timeline: form.timeline,
          },
          settings: {
            remindAssessments:
              typeof previousData.settings?.remindAssessments === "boolean"
                ? previousData.settings.remindAssessments
                : false,
          },
          onboardingCompleted: true,
          createdAt: previousData.createdAt ?? serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Remove obsolete fields saved by previous onboarding versions
      await removeObsoleteProfileFields(ref);

      navigate("/setup-complete", { replace: true });
    } catch (error) {
      console.error(error);
      setErr(error.message || "An error occurred while saving your profile.");
    } finally {
      setBusy(false);
    }
  };

  /*
    The customer may defer onboarding without being falsely marked
    as having completed company setup. No Firestore write is required.
  */
  const skipForNow = () => {
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="landing" style={{ alignItems: "center" }}>
      <main className="landing__main" style={{ maxWidth: 680 }}>
        <h1 className="landing__title">Tell us about your company</h1>

        <p className="landing__subtitle">
          We’ll personalize your ESG journey.
        </p>

        {/* Stepper */}
        <div className="stepper">
          {[1, 2, 3].map((number) => (
            <div
              key={number}
              className={`stepper__seg ${
                number <= step ? "is-active" : ""
              }`}
            />
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <section>
            <h2 className="landing__subtitle" style={{ marginBottom: 8 }}>
              Basics
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <label style={labelStyle}>Sector</label>

                <Select
                  value={form.sector}
                  onChange={(event) => update("sector", event.target.value)}
                >
                  <option value="">Select…</option>

                  {sectors.map((sector) => (
                    <option key={sector} value={sector}>
                      {sector}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label style={labelStyle}>Company size</label>

                <Select
                  value={form.size}
                  onChange={(event) => update("size", event.target.value)}
                >
                  <option value="">Select…</option>

                  {sizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div style={actionsRow}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={skipForNow}
                disabled={busy}
              >
                Skip for now
              </button>

              <div style={{ flex: 1 }} />

              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setStep(2)}
                disabled={!canNext1}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <section>
            <h2 className="landing__subtitle" style={{ marginBottom: 8 }}>
              Regulatory context
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <label style={labelStyle}>Where is your company based?</label>

                <Select
                  value={form.region}
                  onChange={(event) => update("region", event.target.value)}
                >
                  <option value="">Select…</option>

                  {regions.map((region) => (
                    <option key={region.value} value={region.value}>
                      {region.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label style={labelStyle}>CSRD applicability</label>

                <Select
                  value={form.csrd}
                  onChange={(event) => update("csrd", event.target.value)}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Unsure">Unsure</option>
                </Select>
              </div>
            </div>

            <p style={helperTextStyle}>
              EcoTrack is based on European Union sustainability guidance.
              Companies based outside the EU can still use the assessment,
              although local regulatory requirements may differ.
            </p>

            <div style={actionsRow}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setStep(1)}
                disabled={busy}
              >
                Back
              </button>

              <div style={{ flex: 1 }} />

              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setStep(3)}
                disabled={!canNext2 || busy}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <section>
            <h2 className="landing__subtitle" style={{ marginBottom: 8 }}>
              Focus
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <label style={labelStyle}>Main goal</label>

                <Select
                  value={form.goal}
                  onChange={(event) => update("goal", event.target.value)}
                >
                  <option value="compliance">Compliance</option>
                  <option value="funding">Funding / Investors</option>
                  <option value="brand">Brand &amp; Market</option>
                  <option value="supply">Supply chain</option>
                </Select>
              </div>

              <div>
                <label style={labelStyle}>Timeline</label>

                <Select
                  value={form.timeline}
                  onChange={(event) => update("timeline", event.target.value)}
                >
                  <option value="0-6">0–6 months</option>
                  <option value="6-12">6–12 months</option>
                  <option value="12+">12+ months</option>
                </Select>
              </div>
            </div>

            <div style={actionsRow}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setStep(2)}
                disabled={busy}
              >
                Back
              </button>

              <div style={{ flex: 1 }} />

              <button
                type="button"
                className="btn btn--primary"
                onClick={finish}
                disabled={busy}
              >
                {busy ? "Saving…" : "Finish"}
              </button>
            </div>
          </section>
        )}

        {err && (
          <p style={{ color: "#b91c1c", marginTop: 12, fontSize: 14 }}>
            {err}
          </p>
        )}
      </main>
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 13,
  marginBottom: 6,
  color: "#334155",
};

const helperTextStyle = {
  marginTop: 14,
  marginBottom: 0,
  fontSize: 13,
  lineHeight: 1.5,
  color: "#64748b",
};

const actionsRow = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 16,
};